import React from 'react';
import { compose, withHandlers, withStateHandlers } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { UncontrolledTooltip } from 'reactstrap';
import TableGrid from '../../../../../common/components/TableGrid/TableGrid';
import { GridCell } from '../../../../../common/components/Grid/Grid';
import { setRssFeedOrderBy } from "@mm/state/modules/rssFeedTableState/actions";
import { activeChatbotIdSelector } from "@mm/state/selectors/viewSelectors";
import ConfirmDeleteModal from '../../../../components/ConfirmDeleteModal/ConfirmDeleteModal';
import * as routes from '../../../../../common/constants/routes';
import RssFeedCRUDActions from "@mm/state/actions/RssFeedCRUDActions";
import { CONFIRM_DELETE_ENTITY_TEXT } from '../../constants/texts';
import './RssFeedsTableWithFeeds.scss';
import { ThreeDotMenu } from '../../../../../common/components/ThreeDotMenu/ThreeDotMenu';

const actions = [
  { name: 'Edit', value: 'edit' },
  { name: 'Delete', value: 'delete' },
];

const errorTooltip = () => feed => (
  <div>
    <span className="feed-details__error" id={`error-message-${feed.id}`}>
      Error
    </span>
    <UncontrolledTooltip
      className="form-field__tooltip"
      innerClassName="form-field__inner-tooltip"
      target={`error-message-${feed.id}`}
    >
      {feed.errorMessage}
    </UncontrolledTooltip>
  </div>
);

const onDeleteClick = ({ setDeletedFeedId, setShowDeleteModal }) => id => {
  setDeletedFeedId(id);
  setShowDeleteModal(true);
};

const onEditClick = ({ history, chatbotId }) => id =>
  history.push(`${routes.CHATBOT_EDITOR}/${chatbotId}/rss-feeds/edit/${id}`);

const renderDataCell = ({ onEditClick }) => field => item => {
  return field === 'name' ? (
    <GridCell>
      <div className="feeds-table__data table-name" onClick={() => onEditClick(item.id)}><strong>{item[field]}</strong></div>
    </GridCell>
  ) : (
    <GridCell>
      <div className="feeds-table__data">{item[field]}</div>
    </GridCell>
  );
};

const onSelectItem = (action, value, { onEditClick, onDeleteClick }) => {
  if (action === 'edit') {
    onEditClick(value);
  }
  if (action === 'delete') {
    onDeleteClick(value);
  }
};

const renderActionCell = ({ onEditClick, onDeleteClick }) => item => (
  <GridCell overflowable className="action-cell">
    <ThreeDotMenu
      elements={actions}
      selectItem={action =>
        onSelectItem(action, item.id, { onEditClick, onDeleteClick })
      }
    />
  </GridCell>
);

const renderStatusCell = ({ errorTooltip }) => feed => {
  const { active, status } = feed;
  let content;

  if (active) {
    content = status === 'failed' ? errorTooltip(feed) : 'Active';
  } else {
    content = 'Draft';
  }

  return <GridCell>{content}</GridCell>;
};

const getTableData = props => () => {
  const {
    renderDataCell,
    renderActionCell,
    setRssFeedOrderBy,
    renderStatusCell,
  } = props;

  return [
    {
      label: 'Feed Name',
      onChange: setRssFeedOrderBy,
      keyName: 'name',
      renderCell: renderDataCell('name'),
    },
    {
      label: 'Status',
      onChange: setRssFeedOrderBy,
      keyName: 'formattedStatus',
      renderCell: renderStatusCell,
    },
    {
      label: 'RSS Feed',
      onChange: setRssFeedOrderBy,
      keyName: 'formattedUrl',
      renderCell: renderDataCell('url'),
    },
    {
      label: 'Audience',
      onChange: setRssFeedOrderBy,
      keyName: 'formattedAudienceName',
      renderCell: renderDataCell('audienceName'),
    },
    {
      label: 'Updated',
      onChange: setRssFeedOrderBy,
      keyName: 'updatedAt',
      renderCell: renderDataCell('formattedUpdatedAt'),
    },
    {
      label: 'Actions',
      className: 'action-cell',
      renderCell: renderActionCell,
    },
  ];
};

const mapDispatchToProps = dispatch => ({
  setRssFeedOrderBy: rssFeedOrderBy =>
    dispatch(setRssFeedOrderBy(rssFeedOrderBy)),
  onDeleteFeed: (botId, entityId) =>
    dispatch(RssFeedCRUDActions.delete(botId, { id: entityId })),
});

const mapStateToProps = state => ({
  chatbotId: activeChatbotIdSelector(state),
});

const initialState = {
  showDeleteModal: false,
  deletedFeedId: null,
};

const stateUpdaters = {
  setShowDeleteModal: () => showDeleteModal => ({ showDeleteModal }),
  setDeletedFeedId: () => deletedFeedId => ({ deletedFeedId }),
};

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ onDeleteClick, onEditClick, errorTooltip }),
  withHandlers({ renderDataCell, renderActionCell, renderStatusCell }),
  withHandlers({ getTableData }),
);

const RssFeedsTableWithFeeds = props => {
  const {
    getTableData,
    feeds,
    showDeleteModal,
    setShowDeleteModal,
    onDeleteFeed,
    deletedFeedId,
  } = props;

  const data = getTableData();

  return (
    <div className="feeds-table-wrapper">
      <TableGrid orderByAtTheEnd={true} className="feeds-table" data={data} items={feeds} />
      <ConfirmDeleteModal
        headerText={CONFIRM_DELETE_ENTITY_TEXT}
        show={showDeleteModal}
        onDelete={onDeleteFeed}
        selectedEntityId={deletedFeedId}
        onClose={() => {
          setShowDeleteModal(false);
        }}
      />
    </div>
  );
};

export default enhance(RssFeedsTableWithFeeds);
